<template>
  <tbody
    :id="custFormId"
    :class="['ft-field-row', { 'has-errors': hasErrors }]"
  >
    <tr>
      <td class="ft-field-row__handle-cell">
        <button
          type="button"
          class="ft-field-row__handle"
          aria-label="Glisser pour changer l'ordre"
        >
          <svg
            class="ft-field-row__grip"
            width="10"
            height="16"
            viewBox="0 0 10 16"
            aria-hidden="true"
          >
            <g fill="currentColor">
              <circle cx="2" cy="2" r="1.35" />
              <circle cx="8" cy="2" r="1.35" />
              <circle cx="2" cy="8" r="1.35" />
              <circle cx="8" cy="8" r="1.35" />
              <circle cx="2" cy="14" r="1.35" />
              <circle cx="8" cy="14" r="1.35" />
            </g>
          </svg>
        </button>
      </td>
      <td>
        <input
          v-model="form.label.value"
          type="text"
          required
          :maxlength="form.label.field.max_length"
          :name="form.label.html_name"
          aria-label="Libellé affiché"
          @input="onLabelInput"
        >
        <ul class="errorlist">
          <li
            v-for="error in form.label.errors"
            :key="error"
          >
            {{ error }}
          </li>
        </ul>
      </td>
      <td>
        <input
          v-model="form.name.value"
          type="text"
          required
          :maxlength="form.name.field.max_length"
          :name="form.name.html_name"
          aria-label="Nom technique"
          @input="onNameInput"
        >
        <ul class="errorlist">
          <li
            v-for="error in form.name.errors"
            :key="error"
          >
            {{ error }}
          </li>
        </ul>
      </td>
      <td>
        <select
          :value="form.field_type.value"
          aria-label="Type"
          @change="onFieldTypeChange"
        >
          <option
            v-for="choice in customFieldTypeChoices"
            :key="choice.value"
            :value="choice.value"
          >
            {{ choice.name }}
          </option>
        </select>
        <ul class="errorlist">
          <li
            v-for="error in form.field_type.errors"
            :key="error"
          >
            {{ error }}
          </li>
        </ul>
      </td>
      <td class="ft-field-row__mandatory">
        <input
          v-if="form.field_type.value !== 'boolean'"
          v-model="form.is_mandatory.value"
          type="checkbox"
          :aria-label="form.conditional_field_config.value ? 'Obligatoire si actif' : 'Obligatoire'"
          @change="updateStore"
        >
        <span
          v-else
          class="ft-field-row__na"
        >—</span>
      </td>
      <td class="ft-field-row__actions">
        <button
          type="button"
          class="ft-field-row__rules"
          :class="{ 'is-open': rulesOpen, 'has-rules': hasRules }"
          @click="rulesOpen = !rulesOpen"
        >
          Règles
          <i
            :class="['chevron icon', rulesOpen ? 'up' : 'down']"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          class="ft-field-row__remove"
          aria-label="Supprimer le champ"
          @click="removeCustomForm()"
        >
          <img
            src="@/assets/img/project-card/delete.png"
            alt=""
          />
        </button>
      </td>
    </tr>

    <tr
      v-if="showDetails"
      class="ft-field-details"
    >
      <td colspan="6">
        <div
          v-if="form.field_type.value === 'pre_recorded_list'"
          class="ft-field-details__block"
          data-test="prerecorded-list-option"
        >
          <label :for="`prerecorded-${customForm.dataKey}`">Liste pré-enregistrée</label>
          <select
            :id="`prerecorded-${customForm.dataKey}`"
            :value="form.options.value[0] || ''"
            @change="onPrerecordedChange"
          >
            <option
              disabled
              value=""
            >
              Sélectionner une liste
            </option>
            <option
              v-for="listName in preRecordedLists"
              :key="listName"
              :value="listName"
            >
              {{ listName }}
            </option>
          </select>
        </div>

        <div
          v-if="form.field_type.value === 'list' || form.field_type.value === 'multi_choices_list'"
          class="ft-field-details__block"
        >
          <label :for="form.options.id_for_label">{{ form.options.label }}</label>
          <div :id="`list-options-${customForm.dataKey}`">
            <div
              v-for="(option, index) in form.options.value"
              :id="option"
              :key="`${option}-${index}`"
              class="draggable-row"
            >
              <i
                class="th icon grey"
                aria-hidden="true"
              />
              <input
                :value="option"
                type="text"
                class="options-field"
                :maxlength="form.options.field.max_length"
                :name="form.options.html_name"
                @change="updateOptionValue(index, $event)"
              >
              <button
                type="button"
                class="ft-option__remove"
                aria-label="Supprimer cette option"
                @click="deleteOption(index)"
              >
                <img
                  src="@/assets/img/project-card/delete.png"
                  alt=""
                >
              </button>
            </div>
          </div>
          <button
            type="button"
            class="ft-field-details__add"
            @click="addOption"
          >
            + Ajouter une option
          </button>
        </div>
        <ul
          v-if="showOptions"
          class="errorlist"
        >
          <li
            v-for="error in form.options.errors"
            :key="error"
          >
            {{ error }}
          </li>
        </ul>

        <div
          v-if="rulesOpen"
          class="ft-rules"
        >
          <label class="ft-field-details__check">
            <input
              type="checkbox"
              :checked="Boolean(form.conditional_field_config.value)"
              @change="setConditionalCustomForm"
            >
            <span>
              <strong>Activation conditionnelle</strong>
              <small>Le champ n'apparaît dans le formulaire que si la condition est remplie.</small>
            </span>
          </label>

          <div
            v-if="form.conditional_field_config.value !== null && form.conditional_field_config.value !== undefined"
            id="condition-field"
          >
            <CustomFormConditionalField
              :form="form"
              :stored-custom-form="customForm"
              :custom-forms="customForms"
              :config="form.conditional_field_config.value"
              @update:config="setConditionalFieldConfig($event)"
            />
            <ul class="errorlist">
              <li
                v-for="error in form.conditional_field_config.errors"
                :key="error"
              >
                {{ error }}
              </li>
            </ul>
          </div>

          <div class="ft-rules__forced">
            <strong>Valeurs forcées</strong>
            <small>Imposer une valeur à ce champ selon la valeur d'un autre champ.</small>
            <div
              v-for="(config, index) in form.forced_value_config.value"
              :id="`forced-value-${index}`"
              :key="`forced-value-${config.dataKey}`"
              class="ft-forced"
            >
              <CustomFormConditionalField
                :form="form"
                :stored-custom-form="customForm"
                :custom-forms="customForms"
                :config="config"
                :is-forced-value="true"
                @update:config="setForcedValue($event)"
              />
              <button
                type="button"
                class="ft-forced__remove"
                aria-label="Supprimer cette valeur forcée"
                @click="deleteForcedValue(config.dataKey)"
              >
                <img
                  src="@/assets/img/project-card/delete.png"
                  alt=""
                />
              </button>
            </div>
            <ul class="errorlist">
              <li
                v-for="error in form.forced_value_config.errors"
                :key="error"
              >
                {{ error }}
              </li>
            </ul>
            <button
              id="add-forced-value"
              type="button"
              class="ft-field-details__add"
              @click.prevent="addForcedValue"
            >
              + Ajouter une valeur forcée
            </button>
          </div>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Sortable from 'sortablejs';

import { customFieldTypeChoices, reservedKeywords } from '@/utils';
import CustomFormConditionalField from '@/components/FeatureType/CustomFormConditionalField.vue';

export default {
  name: 'FeatureTypeCustomForm',

  components: {
    CustomFormConditionalField,
  },

  props: {
    customForm: {
      type: Object,
      default: null,
    },
    selectedColorStyle: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      customFieldTypeChoices,
      form: {
        is_mandatory: {
          value: false,
          html_name: 'mandatory-custom-field',
        },
        label: {
          errors: [],
          id_for_label: 'label',
          label: 'Label',
          help_text: 'Nom en language naturel du champ',
          html_name: 'label',
          field: {
            max_length: 256,
          },
          value: null,
        },
        name: {
          errors: [],
          id_for_label: 'name',
          label: 'Nom',
          html_name: 'name',
          help_text:
            "Nom technique du champ tel qu'il apparaît dans la base de données ou dans l'export GeoJSON. Seuls les caractères alphanumériques et les traits d'union sont autorisés: a-z, A-Z, 0-9, _ et -)",
          field: {
            max_length: 128,
          },
          value: null,
        },
        position: {
          errors: [],
          id_for_label: 'position',
          label: 'Position',
          min_value: 0, // ! check if good values (not found)
          html_name: 'position',
          help_text:
            "Numéro d'ordre du champ dans le formulaire de saisie du signalement",
          field: {
            max_length: 128, // ! check if good values (not found)
          },
          value: this.customForm.dataKey - 1,
        },
        field_type: {
          errors: [],
          id_for_label: 'field_type',
          label: 'Type de champ',
          html_name: 'field_type',
          help_text: '',
          field: {
            max_length: 50,
          },
          value: 'boolean',
        },
        options: {
          errors: [],
          id_for_label: 'options',
          label: 'Options',
          html_name: 'options',
          help_text: 'Valeurs possibles de ce champ, séparées par des virgules',
          field: {
            max_length: null,
          },
          value: [],
        },
        conditional_field_config: {
          errors: [],
          value: null,
        },
        forced_value_config: {
          errors: [],
          value: [],
        }
      },
      hasErrors: false,
      rulesOpen: false,
      nameTouched: false,
      selectedPrerecordedList: null,
      sortable: null
    };
  },

  computed: {
    ...mapState('feature-type', [
      'customForms',
      'preRecordedLists'
    ]),
    custFormId() {
      return `custom-field-${this.customForm.dataKey}`;
    },
    showOptions() {
      return ['list', 'multi_choices_list', 'pre_recorded_list'].includes(this.form.field_type.value);
    },
    showDetails() {
      return this.rulesOpen || this.showOptions;
    },
    hasRules() {
      const conditional = this.form.conditional_field_config.value;
      const forced = this.form.forced_value_config.value;
      return Boolean(conditional) || (Array.isArray(forced) && forced.length > 0);
    },
    arrayOption: {
      get() {
        return this.form.options.value.join();
      },
      // * create an array, because backend expects an array
      set(newValue) {
        this.form.options.value = this.trimWhiteSpace(newValue).split(',');
        if (!this.hasDuplicateOptions()) {
          this.updateStore();
        }
      },
    },
    forcedValMaxDataKey() { // get the highest datakey value in forced value configs...
      return this.form.forced_value_config.value.reduce( // ...used to increment new config datakey
        (maxDataKey, currentConfig) => (maxDataKey > currentConfig.dataKey) ?
          maxDataKey : currentConfig.dataKey, 1
      );
    },
  },

  mounted() {
    //* add datas from store to state to avoid mutating directly store with v-model (not good practice), could have used computed with getter and setter as well
    this.fillCustomFormData(this.customForm);
    const fieldType = this.form.field_type.value;
    if (fieldType === 'pre_recorded_list' || this.customForm.field_type === 'pre_recorded_list') {
      this.GET_PRERECORDED_LISTS();
    }
    if (fieldType === 'list' || fieldType === 'multi_choices_list') {
      this.initSortable();
    }
    if (this.hasRules) {
      this.rulesOpen = true;
    }
    this.nameTouched = Boolean(this.form.name.value);
  },

  beforeDestroy() {
    if (this.sortable) {
      this.sortable.destroy();
    }
  },

  methods: {
    ...mapActions('feature-type', [
      'GET_PRERECORDED_LISTS'
    ]),

    hasDuplicateOptions() {
      this.form.options.errors = [];
      const isDup =
        new Set(this.form.options.value).size !==
        this.form.options.value.length;
      if (isDup) {
        this.form.options.errors = ['Veuillez saisir des valeurs différentes'];
        return true;
      }
      return false;
    },

    fillCustomFormData(customFormData) {
      for (const el in customFormData) {
        if (el && this.form[el] && customFormData[el] !== undefined && customFormData[el] !== null) {
          //* check if is an object, because data from api is a string, while import from django is an object
          this.form[el].value = customFormData[el].value
            ? customFormData[el].value
            : customFormData[el];
        }
      }
      this.updateStore();
    },

    removeCustomForm() {
      this.$store.commit(
        'feature-type/REMOVE_CUSTOM_FORM',
        this.customForm.dataKey
      );
    },

    setPosition(index) {
      if (this.form.position.value !== index) {
        this.form.position.value = index;
        this.updateStore();
      }
    },

    slugifyTechnicalName(label) {
      return String(label || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/_+/g, '_')
        .toLowerCase();
    },

    onLabelInput() {
      if (!this.nameTouched) {
        this.form.name.value = this.slugifyTechnicalName(this.form.label.value);
      }
      this.updateStore();
    },

    onNameInput() {
      this.nameTouched = true;
      this.updateStore();
    },

    onFieldTypeChange(event) {
      const value = event.target.value;
      if (value === 'pre_recorded_list') {
        this.GET_PRERECORDED_LISTS();
      }
      this.form.field_type.value = value;
      this.form = { ...this.form };
      this.updateStore();
      if (this.sortable) {
        this.sortable.destroy();
        this.sortable = null;
      }
      if (value === 'list' || value === 'multi_choices_list') {
        this.initSortable();
      }
    },

    onPrerecordedChange(event) {
      this.arrayOption = event.target.value;
    },
        
    initSortable() {
      this.$nextTick(() => {
        const el = document.getElementById(`list-options-${this.customForm.dataKey}`);
        if (!el) {
          return;
        }
        if (this.sortable) {
          this.sortable.destroy();
        }
        this.sortable = new Sortable(el, {
          animation: 150,
          handle: '.draggable-row', // The element that is active to drag
          ghostClass: 'blue-background-class',
          dragClass: 'white-opacity-background-class',
          onEnd: this.updateOptionOrder,
          filter: 'input',        // prevent input field not clickable...
          preventOnFilter: false, // ... on element inside sortable
        });
      });
    },

    setConditionalFieldConfig(config) {
      this.form.conditional_field_config.value = config;
      this.updateStore();
    },

    setConditionalCustomForm() {
      if (this.form.conditional_field_config.value === null) {
        // retrieve existing value when the user uncheck and check again, if no value defined create an empty object
        this.form.conditional_field_config.value = this.customForm.conditional_field_config || {};
      } else {
        this.form.conditional_field_config.value = null;
      }
      this.updateStore();
    },
    
    addForcedValue() {
      const newForcedValueConfig = {
        dataKey: this.forcedValMaxDataKey + 1,
        // forced value can already be set here, setting false for boolean because user won't select if he wants to set on false, thus avoiding the null value not passing form check
        forcedValue: this.form.field_type.value === 'boolean' ? false : null
      };
      this.form.forced_value_config.value = [...this.form.forced_value_config.value, newForcedValueConfig];
    },
    
    setForcedValue(newConfig) {
      this.form.forced_value_config.value = this.form.forced_value_config.value.map((config) => {
        return config.dataKey === newConfig.dataKey ? newConfig : config;
      });
      this.updateStore();
    },
    
    deleteForcedValue(dataKey) {
      this.form.forced_value_config.value = this.form.forced_value_config.value.filter(
        (config) => config.dataKey !== dataKey
      );
      this.updateStore();
    },

    updateStore() {
      const data = {
        dataKey: this.customForm.dataKey,
        is_mandatory: this.form.is_mandatory.value,
        label: this.form.label.value,
        name: this.form.name.value,
        position: this.form.position.value,
        field_type: this.form.field_type.value,
        options: this.form.options.value,
        conditional_field_config: this.form.conditional_field_config.value,
        forced_value_config: this.form.forced_value_config.value,
      };
      this.$store.commit('feature-type/UPDATE_CUSTOM_FORM', data);
      if (this.customForm.name === this.selectedColorStyle ) {
        this.$emit('update', this.form.options.value);
      }

      // if there was any error, check if the update fixed it and update errors displayed
      if (this.hasErrors) {
        this.checkCustomForm(true);
      }
    },

    updateOptionValue(index, e) {
      this.form.options.value[index] = e.target.value;
      if (!this.hasDuplicateOptions()) {
        this.updateStore();
      }
    },

    updateOptionOrder(e) {
      const currentOptionsList = Array.from(e.target.childNodes).map((el) => el.id);
      this.form.options.value = currentOptionsList;
      this.updateStore();
    },
    
    addOption() {
      this.form.options.value.push('');
    },

    deleteOption(index) {
      this.form.options.value.splice(index, 1);
    },

    trimWhiteSpace(string) {
      // TODO : supprimer les espaces pour chaque option au début et à la fin QUE à la validation
      return string.replace(/\s*,\s*/gi, ',');
    },

    //* CHECKS *//
    hasRegularCharacters(input) {
      for (const char of input) {
        if (!/[a-zA-Z0-9-_]/.test(char)) {
          return false;
        }
      }
      return true;
    },

    /**
     * Ensures the name entered in the form is unique among all custom field names.
     * This function prevents duplicate names, including those with only case differences, 
     * to avoid conflicts during automatic view generation where names are slugified.
     *
     * @returns {boolean} - Returns true if the name is unique (case insensitive), false otherwise.
     */
    checkUniqueName() {
      const occurences = this.customForms
        .map((el) => el.name.toLowerCase())
        .filter((el) => el === this.form.name.value.toLowerCase());
      return occurences.length === 1;
    },

    checkOptions() {
      if (this.form.field_type.value === 'list') {
        return this.form.options.value.length >= 2 && !this.form.options.value.includes('') ?
          '' : 'Veuillez renseigner au moins 2 options.';
      }
      if (this.form.field_type.value === 'pre_recorded_list') {
        return this.form.options.value.length === 1 ?
          '' : 'Veuillez sélectionner une option.';
      }
      return '';
    },

    isConditionFilled(condition) {
      if (condition) {
        return condition.conditionField && condition.conditionValue !== null && condition.conditionValue !== undefined;
      }
      return true;
    },
    
    isForcedValueFilled() {
      if (this.form.forced_value_config.value) {
        for (const config of this.form.forced_value_config.value) {
          if (!this.isConditionFilled(config) || config.forcedValue === null || config.forcedValue === undefined) {
            return false;
          }
        }
      }
      return true;
    },

    checkCustomForm(noScroll) {
      // reset errors to empty array
      for (const element in this.form) {
        if (this.form[element].errors) this.form[element].errors = [];
      }
      // check each form element
      const optionError = this.checkOptions();
      let isValid = true;
      if (!this.form.label.value) {
        //* vérifier que le label est renseigné
        this.form.label.errors = ['Veuillez compléter ce champ.'];
        isValid = false;
      } else if (!this.form.name.value) {
        //* vérifier que le nom est renseigné
        this.form.name.errors = ['Veuillez compléter ce champ.'];
        isValid = false;
      } else if (!this.hasRegularCharacters(this.form.name.value)) {
        //* vérifier qu'il n'y a pas de caractères spéciaux
        this.form.name.errors = [
          'Veuillez utiliser seulement les caratères autorisés.',
        ];
        isValid = false;
      } else if (reservedKeywords.includes(this.form.name.value)) {
        //* vérifier que le nom du champs ne soit pas un nom réservé pour les propriétés standards d'un signalement
        this.form.name.errors = [
          'Ce nom est réservé, il ne peut pas être utilisé',
        ];
        isValid = false;
      } else if (!this.checkUniqueName()) {
        //* vérifier si les noms sont pas dupliqués
        this.form.name.errors = [
          'Les champs personnalisés ne peuvent pas avoir des noms similaires.',
        ];
        isValid = false;
      } else if (optionError) {
        //* s'il s'agit d'un type liste, vérifier que le champ option est bien renseigné
        this.form.options.errors = [optionError];
        isValid = false;
      } else if (this.hasDuplicateOptions()) {
        //* pour le cas d'options dupliqués
        isValid = false;
      } else if (!this.isConditionFilled(this.form.conditional_field_config.value)) {
        //* vérifier si les deux formulaires de l'activation conditionnelle sont bien renseignés
        isValid = false;
        this.form.conditional_field_config.errors = ['Veuillez renseigner tous les champs de la condition ou la désactiver'];
      } else if (!this.isForcedValueFilled()) {
        //* vérifier si les trois formulaires de chaque valeur forcée sont bien renseignés
        isValid = false;
        this.form.forced_value_config.errors = ['Veuillez renseigner tous les champs de chaque condition à valeur forcée ou supprimer celle(s) incomplète(s)'];
      }
      this.hasErrors = !isValid;
      if (!isValid) {
        if (
          this.form.conditional_field_config.errors.length ||
          this.form.forced_value_config.errors.length
        ) {
          this.rulesOpen = true;
        }
        if (!noScroll) {
          this.$nextTick(() => {
            const row = document.getElementById(this.custFormId);
            if (row) {
              row.scrollIntoView();
            }
          });
        }
      }
      return isValid;
    },
  },
};
</script>

<style lang="less" scoped>
.ft-field-row {
  scroll-margin-top: 5rem;

  td {
    padding: 0.45rem 0.55rem;
    border-top: 1px solid #e1e4ea;
    vertical-align: top;
    background: #fff;
  }

  input[type='text'],
  select {
    width: 100%;
    height: 34px;
    border: 1px solid #cfd3dc;
    border-radius: 4px;
    padding: 0 0.5rem;
    font: inherit;
    font-size: 14px;
    background: #fff;
    color: #1c2430;
  }

  &.has-errors td {
    background: #fff8f7;
  }
}

.ft-field-row__handle-cell {
  width: 2rem;
  text-align: center;
}

.ft-field-row__handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #2c3344;
  cursor: grab;
  padding: 0.35rem 0.15rem;
}

.ft-field-row__grip {
  display: block;
}

.ft-field-row__mandatory {
  width: 6.5rem;
  text-align: center;
  padding-top: 0.7rem !important;

  input {
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    width: 1.05rem;
    height: 1.05rem;
    margin: 0;
    border: 1.5px solid #c5cad3;
    border-radius: 2px;
    background: #fff;
    cursor: pointer;

    &:checked {
      border-color: #B52222;
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 0.3rem;
      top: 0.08rem;
      width: 0.3rem;
      height: 0.58rem;
      border: solid #B52222;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}

.ft-field-row__na {
  color: #9aa1ab;
}

.ft-field-row__actions {
  width: 8.5rem;
  white-space: nowrap;
}

.ft-field-row__rules,
.ft-field-row__remove {
  border: 1px solid #d5d8e0;
  background: #fff;
  color: #3A468E;
  border-radius: 4px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  height: 34px;
}

.ft-field-row__rules {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0 0.55rem;
  margin-right: 0.35rem;

  &.is-open {
    background: #eef0f8;
    border-color: #3A468E;
  }
}

.ft-field-row__remove {
  width: 34px;
  padding: 0;

  img {
    width: 1.15rem;
    height: 1.15rem;
    object-fit: contain;
  }
}

.ft-field-details td {
  background: #f8f9fb;
  padding: 0.75rem 0.9rem 0.9rem 2.4rem;
}

.ft-field-details__block {
  margin-bottom: 0.75rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.35rem;
  }

  select {
    max-width: 28rem;
    height: 34px;
    border: 1px solid #cfd3dc;
    border-radius: 4px;
    padding: 0 0.5rem;
    font: inherit;
  }

  h5 {
    margin: 0.8rem 0 0.4rem;
  }
}

.ft-field-details__check {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-weight: 600;
  cursor: pointer;

  input {
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    flex: none;
    width: 1.05rem;
    height: 1.05rem;
    margin: 0.2rem 0 0;
    border: 1.5px solid #c5cad3;
    border-radius: 2px;
    background: #fff;
    cursor: pointer;

    &:checked {
      border-color: #B52222;
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 0.3rem;
      top: 0.08rem;
      width: 0.3rem;
      height: 0.58rem;
      border: solid #B52222;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  small {
    display: block;
    margin-top: 0.15rem;
    font-weight: 400;
    color: #5c6570;
  }
}

.ft-rules {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.ft-rules__forced {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  small {
    color: #5c6570;
    font-weight: 400;
  }
}

.ft-forced {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ft-forced__remove,
.ft-option__remove {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #d5d8e0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  padding: 0;

  img {
    width: 1.15rem;
    height: 1.15rem;
    object-fit: contain;
  }
}

.ft-field-details__add {
  margin-top: 0.45rem;
  border: 0;
  background: transparent;
  color: #3A468E;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

#condition-field {
  padding-left: 1.5em;
}

.errorlist {
  margin: 0.3rem 0 0;
  padding: 0;
  list-style: none;
  color: #c0392b;
}

i.icon.trash {
  transition: color ease .3s;
  cursor: pointer;
}

i.icon.trash:hover {
  color: red !important;
}

.draggable-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.55em;

  input {
    margin: 0 0.5em !important;
    height: 34px;
    border: 1px solid #cfd3dc;
    border-radius: 4px;
    padding: 0 0.5rem;
    flex: 1;
  }
}
</style>
